<template>
    <basic-card>
        <template #header>
            {{ definitionObject.name }}
        </template>
        <template>
            <form-wrapper>
                <form-select-input :caption="$t('comparison.operationDefinition')"
                                   :options="operationDefinitionOptions"
                                   v-model="definitionObject.operationDefinitionId"/>
            </form-wrapper>


            <framed-content v-for="(searchQueryWithOperator, i) in definitionObject.searchQueriesWithOperators"
                            :title="$t('comparison.filterLabel', {index : searchQueryWithOperator.searchQuery.name})"
                            :key="i">
                <form-wrapper>
                    <form-select-input :caption="$t('comparison.operator')"
                                       :options="searchQueryOperatorOptions"
                                       v-model="searchQueryWithOperator.operator"/>
                    <form-select-input :caption="$t('searchQuery.loadQuery')"
                                       :value="searchQueryWithOperator.searchQuery.id"
                                       @input="newId => handleSearchQuerySelected(newId, searchQueryWithOperator)"
                                       :options="getSearchQueryOptionsBySelected(searchQueryWithOperator.searchQuery.id)"/>
                    <form-text-input :caption="$t('searchQuery.queryName')"
                                     v-model="searchQueryWithOperator.searchQuery.name"/>
                </form-wrapper>

                <filter-form :filter-definition="filterDefinition"
                             :value="searchQueryWithOperator.searchQuery"
                             @input="newQuery => handleFilterObjectChanged(newQuery, searchQueryWithOperator)"
                             :key="`i + ${searchQueryWithOperator.searchQuery.id}`"/>
                <simple-horizontal-layout>
                    <simple-button @press="saveFilter(searchQueryWithOperator.searchQuery)">{{
                            $t("searchQuery.saveQuery")
                        }}
                    </simple-button>
                    <simple-button @press="clearFilter(searchQueryWithOperator.searchQuery)">
                        {{ $t("searchQuery.clearQuery") }}
                    </simple-button>
                    <simple-button @press="removeSearchQueryWithOperator(i)">{{ $t('comparison.removeFilter') }}
                    </simple-button>
                </simple-horizontal-layout>


            </framed-content>


            <form-wrapper>
                <form-text-input :caption="$t('comparison.expression')" v-model="definitionObject.expression"/>
                <form-text-input :caption="$t('comparison.expressionValue')"
                                 disabled
                                 v-model="definitionObject.expressionValue"/>
            </form-wrapper>


            <simple-layout horizontal>
                <simple-button @press="addNewSearchQueryWithOperator"
                               :disabled="!definitionObject.operationDefinitionId">
                    {{ $t('comparison.addNewSearchQuery') }}
                </simple-button>
                <simple-button @press="handleRunCalculation" :disabled="loadingData">{{
                        $t('comparison.run')
                    }}
                </simple-button>
            </simple-layout>
            <loader style="margin: 8px 0;" :message="$t('comparison.loading')" v-show="loadingData"/>


            <framed-content :title="$t('comparison.list')" :default-collapsed="true" class="width-class">
                <data-table
                  :key="definitionObject.operationDefinitionId"
                  :data="definitionObject.operationsList"
                  :configuration="operationsTableConfig"
                  :component-id="`operations[${definitionObject.operationDefinitionId}]-list`"
                />
            </framed-content>
        </template>
    </basic-card>


</template>

<script>
import BasicCard from "../components/layout/BasicCard";
import DataTable from "../components/table/DataTable";
import FormWrapper from "@/components/form/FormWrapper";
import FormSelectInput from "@/components/form/FormSelectInput";
import { mapGetters } from "vuex";
import SimpleLayout from "@/components/layout/SimpleLayout";
import Empty from "@/components/layout/Empty";
import SimpleButton from "@/components/button/SimpleButton";
import FormTextInput from "@/components/form/FormTextInput";
import FramedContent from "@/components/layout/FramedContent";
import { evaluate } from "mathjs";
import Loader from "@/components/Loader";
import SimpleHorizontalLayout from "@/components/layout/SimpleHorizontalLayout";
import QueryUtils from "@/utils/QueryUtils";
import FilterForm from "@/components/filters/FilterForm";

export default {
    name: "ComparisonCard",
    components: {
        FilterForm,
        SimpleHorizontalLayout,
        Loader,
        FramedContent,
        FormTextInput,
        SimpleButton,
        Empty,
        SimpleLayout,
        FormSelectInput,
        FormWrapper,
        DataTable,
        BasicCard
    },
    props: {
        dataObject: {
            type: Object,
            default: () => {
            }
        }
    },
    data: function () {
        return {
            loadingData: false,
            definitionObject: {
                operationDefinitionId: null,
                searchQueriesWithOperators: [], // { operator : 'disjunction', searchQuery: searchQuery }
                expression: '',
                expressionValue: 0,
                operationsList: []
            },

            searchQueryOperatorOptions: [
                {
                    id: 'add',
                    name: 'Přidat množinu'
                },
                {
                    id: 'remove',
                    name: 'Odebrat množinu'
                },

            ],
            operationsTableConfig: {
                columns: [
                    {
                        caption: this.$t('status'),
                        datum: d => d.actualStatus,
                        style: d => d.modified ? this.modifiedStyles : undefined
                    },
                    {
                        caption: "Id",
                        datum: d => d.id.substr(d.id.length - 5),
                        style: d => d.modified ? this.modifiedStyles : undefined
                    }
                ],

            },
        }
    },
    computed: {
        ...mapGetters({
            account: "banking/account",
            getOperationDefinition: "operationDefinitions/operationDefinition",
            operationDefinitionOptions: "operationDefinitions/operationDefinitions",
            operationSearchQueries: "operationQueries/operationQueries",
            getPreference: "userPreferences/getPreference",

            currencies: "currencies/currencies",
            accounts: "banking/accounts",
            parties: "parties/parties",
            users: "users/users",
        }),

        numberFormat() {
            const decimalPlaces = this.getPreference('defaultDecimalPlaces');
            return `0,0.${'0'.repeat(decimalPlaces)}`;
        },

        currencyOptions() {
            return this.currencies.map(ccy => ({id: ccy.code, name: ccy.code}));
        },
        accountOptions() {
            return this.accounts.map(acc => ({id: acc.id, name: acc.name}))
        },
        partyOptions() {
            return this.parties.map(pty => ({id: pty.id, name: pty.displayName == null ? "N/A" : pty.displayName}))
        },
        userOptions() {
            return this.users.map(usr => ({id: usr.id, name: usr.username}))
        },
        filterDefinition() {

            const actualOperationDefinition = this.getOperationDefinition(this.definitionObject.operationDefinitionId);

            return QueryUtils.OperationQueryDefinition(actualOperationDefinition, {
                accounts: this.accountOptions,
                currencies: this.currencyOptions,
                parties: this.partyOptions,
                users: this.userOptions
            });
        },

    },
    methods: {

        handleSearchQuerySelected(newId, searchQueryWithOperator) {
            searchQueryWithOperator.searchQuery = this.$_.cloneDeep(this.operationSearchQueries).find(query => query.id === newId);
        },

        handleFilterObjectChanged(newQuery, searchQueryWithOperator) {
            searchQueryWithOperator.searchQuery = newQuery;
        },

        getSearchQueryOptionsBySelected(selectedSearchQueryId) {

            const selectedSearchQueryIds = this.definitionObject.searchQueriesWithOperators.map(searchQueryWithOperator => searchQueryWithOperator.searchQuery.id);

            const filteredSearchQuery = this.$_.cloneDeep(this.operationSearchQueries)
              .filter(query => query.parameters.operationDefinition.value === this.definitionObject.operationDefinitionId)
              .filter(query => !selectedSearchQueryIds.includes(query.id));

            let finalSearchQueries = filteredSearchQuery;

            if (selectedSearchQueryId) {
                const selectedSearchQuery = this.$_.cloneDeep(this.operationSearchQueries).find(query => query.id === selectedSearchQueryId);
                finalSearchQueries = [selectedSearchQuery, ...filteredSearchQuery];
            }

            return finalSearchQueries.filter(query => query).map(query => ({
                id: query.id,
                name: query.name == null ? "No-name" : query.name
            }))
        },


        addNewSearchQueryWithOperator() {
            this.definitionObject.searchQueriesWithOperators.push({operator: 'add', searchQuery: {id: null}})
        },

        removeSearchQueryWithOperator(searchQueryWithOperatorIndex) {
            this.definitionObject.searchQueriesWithOperators.splice(searchQueryWithOperatorIndex, 1);
        },

        async handleRunCalculation() {

            const saveAllSearchQuery = async () => {
                await Promise.all(this.definitionObject.searchQueriesWithOperators.map(searchQueryWithOperator => this.saveFilter(searchQueryWithOperator.searchQuery)))
            }


            const evaluateExpressionOnOperationsList = async () => {

                const mapPropertiesInExpressionToColumns = () => {

                    return propertiesInExpression.map(property => {
                        const propertyName = property.substring(1, property.length - 1);
                        const column = this.operationsTableConfig.columns.find(column => column.systemName === propertyName);

                        if (column === undefined) {
                            return;
                        }

                        return {
                            replacingPropertyName: property,
                            columnLambda: column.datum
                        }

                    })

                }

                const replacePropertiesNamesWithValues = () => {
                    return this.definitionObject.operationsList.map(operation => {

                        let originalExpression = this.definitionObject.expression;

                        // console.log('XXX', this.definitionObject.expression)
                        for (const propertyWithLambda of propertiesWithColumnLambda) {

                            const substitute = this.$numeral(propertyWithLambda.columnLambda(operation)).value().toString();

                            originalExpression = originalExpression.replace(propertyWithLambda.replacingPropertyName, substitute)
                        }

                        return originalExpression;
                    })
                }

                const propertiesInCurlyBrackets = /{([^}]*)}/gi;

                const propertiesInExpression = this.definitionObject.expression?.match(propertiesInCurlyBrackets) ?? [];

                const propertiesWithColumnLambda = mapPropertiesInExpressionToColumns();

                const containsBadPropertyName = propertiesWithColumnLambda.some(property => !property);

                if (containsBadPropertyName) {

                    this.loadingData = false;
                    this.definitionObject.expressionValue = '';

                    this.notify(this.$t('comparison.calculateError'), 'error');
                    return;
                }

                const expressionsPerOperation = replacePropertiesNamesWithValues();

                const sumOfExpressions = expressionsPerOperation.reduce((finalValue, expression) => {
                    finalValue += evaluate(expression);
                    return finalValue;
                }, 0);

                this.definitionObject.expressionValue = this.$numeral(sumOfExpressions).format(this.numberFormat);

                this.loadingData = false;
                this.notify(this.$t('comparison.calculateComplete'), 'ok');

            }


            this.loadingData = true;

            //0. check validity
            if (!this.definitionObject.expression) {

                this.loadingData = false;

                this.notify(this.$t('comparison.emptyExpression'), 'error');
                return;
            }
            //1. save all search query
            await saveAllSearchQuery();

            //2. for new searchQuery find ID
            for (const searchQueryWithOperator of this.definitionObject.searchQueriesWithOperators)
                searchQueryWithOperator.searchQuery = this.$_.cloneDeep(this.operationSearchQueries).find(query => query.id === searchQueryWithOperator.searchQuery.id || query.name === searchQueryWithOperator.searchQuery.name);

            this.notify(this.$t('comparison.calculateStart'), 'ok');

            this.$emit('calculation', this.definitionObject, evaluateExpressionOnOperationsList);

        },

        notify(title, type, message) {
            this.$notify({
                group: 'application',
                title: title,
                text: message ?? title,
                type: type
            })
        },

        async saveFilter(savingSearchQuery) {

            let toSave = this.$_.cloneDeep(savingSearchQuery);

            toSave.parameters.operationDefinition = {
                operator: "eq",
                value: this.definitionObject.operationDefinitionId,
                type: "string"
            };

            if (!toSave.name) {
                this.loadingData = false;
                this.notify(this.$t('comparison.emptyName'), 'error')
                return;
            }


            toSave.object = "operation";

            try {
                await this.$store.dispatch("operationQueries/saveOperationQuery", toSave)
                this.notify(this.$t('comparison.searchQuerySaveComplete'), 'ok', this.$t('comparison.searchQuerySaveComplete', {name: toSave.name}));
            } catch (error) {
                console.warn(error);
                this.notify(this.$t('comparison.searchQuerySaveError'), 'error')
            }
        },

        clearFilter(searchQuery) {
            console.dir(searchQuery)


            this.$set(searchQuery, 'parameters', {
                actualStatus: {
                    type: 'enum',
                    operator: 'in',
                    value: 'LIVE',
                    negation: false
                }
            })


            this.$set(searchQuery, 'parameters.operationDefinition', {
                operator: "eq",
                value: this.definitionObject.operationDefinitionId,
                type: "string"
            })


            console.dir(searchQuery)
            console.log('CLEAR');
        }
    },
    watch: {
        dataObject: {
            handler(newValue) {
                this.$set(this, 'definitionObject', this.$_.cloneDeep(newValue));
                // this.definitionObject.expression = '{amount}/{amountTax}'
            },
            deep: true,
            immediate: true,
        },
        'definitionObject.operationDefinitionId': {
            handler(newValue, oldValue) {

                if (newValue && oldValue) {
                    this.definitionObject.searchQueriesWithOperators = [];
                    this.definitionObject.expression = '';
                    this.definitionObject.expressionValue = '';
                }

                const actualOperationDefinition = this.getOperationDefinition(newValue);
                const operationCache = {};

                const defaultVal = par => d => d.content[par.name];
                const styleLambdas = {
                    STRING: undefined,
                    DATE: undefined,
                    DECIMAL: d => ({'text-align': 'right'}),
                    CURRENCY: undefined,
                };
                const valueLambdas = {
                    STRING: defaultVal,
                    ENUM: par => d => {
                        let value = d.content[par.name];
                        let enumOption = par.details.enumValues.find(op => op.value == value);
                        if (enumOption === undefined) return value;
                        else return enumOption.name;
                    },
                    DATE: par => d => this.$formats.formatDate(defaultVal(par)(d), false),
                    DECIMAL: par => d => this.$numeral(d.content[par.name] / Math.pow(10, par.details.precision)).format('0,0.' + '0'.repeat(par.details.precision)),
                    INTEGER: par => d => this.$numeral(d.content[par.name]).format('0'),
                    BOOLEAN: par => d => d.content[par.name] ? this.$t("yes") : this.$t("no"),
                    GEO_POINT: par => d => {
                        if (!d.content[par.name]) {
                            return "N/A"
                        }
                        return `${this.$formats.formatAmount(d.content[par.name]?.longtitude)}/${this.$formats.formatAmount(d.content[par.name]?.latitude)}`;
                    },
                    ENTITY_REFERENCE: par => d => {
                        let ref_id = d.content[par.name];
                        if (par.details.entityType == 'account') {
                            let acc = this.account(ref_id) ?? "N/A";
                            return acc.name
                        }
                        if (par.details.entityType == 'party') {
                            let party = this.party(ref_id) ?? "N/A";
                            return party.displayName
                        }
                        if (par.details.entityType == 'operation') {
                            return this.cachedOperations[ref_id] ?? "N/A"
                        }
                        return ref_id;
                    },
                    DYNA_MODEL: par => d => "Complex Object {...}",
                    ARRAY: par => d => `${d.content[par.name].length} items`
                }


                Object.keys(actualOperationDefinition.model.dynaProperties)
                  .map(key => actualOperationDefinition.model.dynaProperties[key])
                  .forEach(async par => {
                      if (par.details.type === 'ENTITY_REFERENCE') {
                          const data = await this.$store.dispatch("operations/fetchOperations", {
                              requestedPage: 0,
                              query: par.details.entityFilter,
                              sort: undefined,
                              size: 100000
                          })
                          data.list.forEach(op => operationCache[op.id] = op.name ?? op.id)
                      }


                      this.operationsTableConfig.columns.push({
                          caption: par.displayName,
                          systemName: par.name,
                          datum: valueLambdas[par.details.type](par),
                          style: d => ({
                              ...styleLambdas[par.details.type] ?? (d),
                              ...d.modified ? this.modifiedStyles : undefined
                          }),
                          sortField: `content.values.${par.name}`,
                          allowSort: true
                      })
                  })


            },
            immediate: true,

        }
    }
}
</script>


<style scoped>
.width-class {
    width: calc(100vw - 435px);
}
</style>
